import { FUEL, KERB_LANE } from '@accra/shared';
import { UI_TEXT } from '../../content/lines/ui';
import type { RunState } from '../../game/RunState';
import { isSettledIn } from '../../systems/lanes';
import { STOP_BOX_HALF, stopAt } from '../../systems/passengers/stopSequence';
import { STATION_HALF, nextStation } from '../../world/fuelStations';
import { el, setText } from '../components/el';

const SHOW_WITHIN = 160;

/** Guidance for the stop: get in the kerb lane, brake, boarding progress. */
export class StopPrompt {
  private readonly root = el('div', 'stop-prompt');

  constructor(parent: HTMLElement) {
    parent.append(this.root);
  }

  update(state: RunState): void {
    const { text, urgent } = this.message(state);
    setText(this.root, text);
    this.root.classList.toggle('show', text !== '');
    this.root.classList.toggle('urgent', urgent);
  }

  /** What to tell the driver right now: a police checkpoint first, then the petrol station, then the bus stop. */
  private message(state: RunState): { text: string; urgent: boolean } {
    const police = this.police(state);
    if (police) return police;
    const fuel = this.fuel(state);
    if (fuel) return fuel;
    const text = this.stop(state);
    return { text, urgent: text === UI_TEXT.brakeToLoad };
  }

  private police(state: RunState): { text: string; urgent: boolean } | null {
    const cp = state.checkpoint.current;
    if (!cp) return null;
    if (cp.phase === 'checking' && cp.dash === 'asking') return null;
    if (cp.phase === 'checking') return { text: '🚔 Police checking your papers…', urgent: false };
    if (cp.phase !== 'approach') return null;
    const gap = Math.round(cp.s - state.bus.s - state.bus.size.length / 2);
    if (gap > 170 || gap < 0) return null;
    return { text: `🚔 Police checkpoint ${gap} m · STOP!`, urgent: gap < 70 };
  }

  private fuel(state: RunState): { text: string; urgent: boolean } | null {
    const { fuel, bus } = state;
    if (fuel.filling) return { text: `⛽ Filling… ${Math.round(fuel.units)}%`, urgent: false };
    const station = nextStation(bus.s);
    // Only nag about petrol when the tank is getting low.
    if (!station || fuel.units > 60) return null;
    const distance = Math.round(station.position - bus.s);
    if (distance > 170 || distance < -STATION_HALF) return null;
    const text = isSettledIn(bus.x, KERB_LANE, 0.9) ? `⛽ ${station.name} ${distance} m · stop at the pump` : `⛽ ${station.name} ${distance} m · ${UI_TEXT.keepRight}`;
    return { text, urgent: fuel.units <= FUEL.lowAt };
  }

  private stop(state: RunState): string {
    const { bus, stop, passengers } = state;
    const point = stopAt(stop.seq);
    const distance = point.position - bus.s;
    const dropping = passengers.filter((p) => p.destination === stop.seq).length;
    if (stop.waiting === 0 && dropping === 0 && !stop.stopped) return '';
    if (stop.stopped) {
      if (stop.alightQueue.length > 0) return 'Passengers getting down…';
      return stop.waiting > 0 || state.walkers.some((w) => w.kind === 'board') ? `Boarding… ${passengers.length}/${bus.capacity}` : '';
    }
    if (distance > SHOW_WITHIN || distance < -STOP_BOX_HALF) return '';
    if (!isSettledIn(bus.x, KERB_LANE, 0.9)) return `${point.stop.name} ${Math.round(distance)} m · ${UI_TEXT.keepRight}`;
    if (distance < 45) return UI_TEXT.brakeToLoad;
    const who = dropping > 0 ? `${dropping} getting down` : `${stop.waiting} waiting`;
    return `${point.stop.name} ${Math.round(distance)} m · ${who}`;
  }
}
